import { createFileRoute } from "@tanstack/react-router";
import { CustomRequestWizard } from "@/components/site/custom-request-wizard";
import { getProduct } from "@/data/products";
import { processSteps } from "@/data/content";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { pageUrl } from "@/data/site";

type Search = { ref?: string };

export const Route = createFileRoute("/design-your-piece")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    ref: typeof search.ref === "string" ? search.ref : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Design Your Piece | Custom Order | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "Describe the handcrafted piece you have in mind. Paul reviews every request personally, confirms the details and pricing, then sends a PayPal invoice.",
      },
      { property: "og:title", content: "Design your piece with Paul" },
      {
        property: "og:description",
        content: "A short guided request. No account, no payment until Paul has confirmed the details with you.",
      },
      { property: "og:url", content: pageUrl("/design-your-piece") },
    ],
    links: [{ rel: "canonical", href: pageUrl("/design-your-piece") }],
  }),
  component: DesignYourPiece,
});

function DesignYourPiece() {
  const { ref } = Route.useSearch();
  const referenced = ref ? getProduct(ref) : undefined;

  return (
    <>
      <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Design Your Piece" }]} />
      <PageHeader
        eyebrow="Design your piece"
        title="Tell Paul what you have in mind."
        lede="Ten short steps, most of them a single tap. You can go back at any point without losing what you have entered, and nothing is ordered or charged by sending this."
      />

      <Section tone="paper" className="pt-0">
        <div className="shell grid gap-10 lg:grid-cols-[1.6fr_0.9fr] lg:items-start">
          <div className="-mt-6">
            {referenced && (
              <div className="mb-6 flex items-center gap-4 border border-hairline bg-background p-4">
                <img
                  src={referenced.images[0].src}
                  alt={referenced.images[0].alt}
                  width={160}
                  height={120}
                  className="size-20 shrink-0 object-cover"
                />
                <div className="min-w-0">
                  <p className="eyebrow text-[0.62rem]">Inspired by a previous creation</p>
                  <p className="mt-1 truncate font-semibold">{referenced.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Paul will use this as a reference point, not a fixed specification.
                  </p>
                </div>
              </div>
            )}
            <CustomRequestWizard reference={referenced?.name} />
          </div>

          <aside className="lg:sticky lg:top-28">
            <SectionHeading level={3} eyebrow="What happens next" title="After you press send." />
            <ol className="mt-6 space-y-5">
              {processSteps.map((s) => (
                <li key={s.n} className="border-t border-hairline pt-4">
                  <p className="text-sm font-semibold">
                    <span className="mr-2 text-brass-ink">{s.n}</span>
                    {s.title}
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                </li>
              ))}
            </ol>
            <p className="mt-6 border-t border-hairline pt-4 text-xs text-muted-foreground">
              Your details are used only to reply to this request. Paul does not sell or share them.
            </p>
          </aside>
        </div>
      </Section>
    </>
  );
}
